"use client";

import clsx from "clsx";
import { JSX, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v7 as uuidv7 } from "uuid";
import Button from "@/components/library/Button";
import Icon from "@/components/library/Icon";
import Input from "@/components/library/Input";
import VariableEditor from "@/components/library/VariableEditor";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useAlert } from "@/hooks/useAlert";
import { useOrg } from "@/hooks/useOrg";
import { VariablesResponse } from "@/types/agencies";
import { PhoneScript } from "@/types/campaigns";
import { UseCase, UseCaseAction } from "@/types/scripts";
import { apiLive, apiFetch } from "@/lib/api";
import { GAIL_API } from "@/utils/common/constants/routes";
import {
  putOnboardingPhoneScript,
  withPreviewUseCases,
} from "@/utils/onboarding/onboardingScriptApi";
import { scriptApiVersionFromFeatures } from "@/utils/org/scriptsApiVersion";
import {
  ScriptPreviewMode,
  VARIABLE_REGEX,
  getTranslationPrefix,
} from "@/utils/onboarding/scriptPreviewUtils";
import { associationsFromEmbeddedUseCases } from "@/utils/scripts/embeddedUseCases";

interface ScriptPreviewSectionProps {
  readonly script: PhoneScript | null;
  readonly useCases: UseCase[];
  readonly onScriptUpdate?: (updatedScript: PhoneScript) => void;
  readonly onUseCasesUpdate?: (useCases: UseCase[]) => void;
  readonly mode?: ScriptPreviewMode;
}

// Render content with variable highlighting
function renderContentWithVariables(
  text: string,
  translateFn: (key: string) => string
): (string | JSX.Element)[] | string {
  const variableRegex = new RegExp(VARIABLE_REGEX.source, VARIABLE_REGEX.flags);
  const parts: (string | JSX.Element)[] = [];
  let lastIndex = 0;
  let match;

  while ((match = variableRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const display = match[1];
    parts.push(
      <span
        key={match.index}
        className="inline-block px-1.5 py-0.5 rounded bg-primary/20 text-primary text-xs font-medium"
      >
        {translateFn(display) || display}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}

// Editable Speech bubble component for greeting/goodbye/voicemail
function EditableSpeechBubble({
  content,
  label,
  icon,
  colorClass,
  onSave,
  isSaving,
  variables,
  translationPrefix,
}: {
  readonly content: string;
  readonly label: string;
  readonly icon: ReactNode;
  readonly colorClass: string;
  readonly onSave: (newContent: string) => Promise<void>;
  readonly isSaving: boolean;
  readonly variables: string[];
  readonly translationPrefix: string;
}) {
  const { t } = useTranslation("onboarding");
  const { t: ct } = useTranslation("common");
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(content);

  const handleSave = async () => {
    await onSave(editedContent);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedContent(content);
    setIsEditing(false);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div
            className={clsx("w-8 h-8 rounded-full flex items-center justify-center", colorClass)}
          >
            {icon}
          </div>
          <span className="text-sm font-medium text-700">{label}</span>
        </div>
        {!isEditing && (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className={clsx(
              "p-1.5 rounded-md transition-colors",
              "text-muted-foreground hover:text-foreground hover:bg-100"
            )}
            title={t(`${translationPrefix}.edit`)}
          >
            <Icon icon="pencil" className="w-4 h-4" />
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="ml-4 space-y-3">
          <VariableEditor
            value={editedContent}
            setValue={setEditedContent}
            variables={variables}
            placeholder={t(`${translationPrefix}.enterText`)}
          />
          <div className="flex justify-end gap-2">
            <Button type="tertiary" onClick={handleCancel} disabled={isSaving}>
              {t(`${translationPrefix}.cancel`)}
            </Button>
            <Button
              type="primary"
              onClick={handleSave}
              disabled={isSaving || !editedContent.trim()}
            >
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t(`${translationPrefix}.saving`)}
                </span>
              ) : (
                t(`${translationPrefix}.save`)
              )}
            </Button>
          </div>
        </div>
      ) : (
        <div
          className={clsx(
            "relative ml-4 p-4 rounded-2xl rounded-tl-sm",
            "bg-100 dark:bg-800 border border-200"
          )}
        >
          <p className="text-sm text-foreground whitespace-pre-wrap">
            {renderContentWithVariables(content, ct)}
          </p>
        </div>
      )}
    </div>
  );
}

// Editable use case card component
function EditableUseCaseCard({
  useCase,
  index,
  onSave,
  onDelete,
  isSaving,
  variables,
  translationPrefix,
}: {
  readonly useCase: UseCase;
  readonly index: number;
  readonly onSave: (updatedUseCase: UseCase) => Promise<void>;
  readonly onDelete: (useCaseId: string) => Promise<void>;
  readonly isSaving: boolean;
  readonly variables: string[];
  readonly translationPrefix: string;
}) {
  const { t } = useTranslation("onboarding");
  const { t: ct } = useTranslation("common");
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(useCase.title);
  const [editedDescription, setEditedDescription] = useState(useCase.description);
  const actionKeyCounter = useRef(0);

  const ensureKeys = (items: UseCaseAction[]): UseCaseAction[] =>
    items.map((a) => (a._key ? a : { ...a, _key: `action-${++actionKeyCounter.current}` }));

  const [editedActions, setEditedActions] = useState<UseCaseAction[]>(() =>
    ensureKeys(useCase.actions || [])
  );

  // Get actions with stable keys for display
  const actions = editedActions;

  const handleSave = async () => {
    await onSave({
      ...useCase,
      title: editedTitle,
      description: editedDescription,
      actions: editedActions,
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedTitle(useCase.title);
    setEditedDescription(useCase.description);
    setEditedActions(ensureKeys(useCase.actions || []));
    setIsEditing(false);
  };

  const handleActionChange = (idx: number, value: string) => {
    const newActions = [...editedActions];
    newActions[idx] = { ...newActions[idx], description: value };
    setEditedActions(newActions);
  };

  const handleAddAction = () => {
    setEditedActions([
      ...editedActions,
      { description: "", _key: `action-${++actionKeyCounter.current}` },
    ]);
  };

  const handleRemoveAction = (idx: number) => {
    setEditedActions(editedActions.filter((_, i) => i !== idx));
  };

  if (isEditing) {
    return (
      <div
        className={clsx(
          "border border-primary/50 rounded-xl overflow-hidden",
          "bg-panel p-4 space-y-4"
        )}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={clsx(
                "w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0",
                "bg-primary/10 text-primary text-xs font-semibold"
              )}
            >
              {index + 1}
            </div>
            <span className="text-sm font-medium text-700">
              {t(`${translationPrefix}.editingScenario`)}
            </span>
          </div>
        </div>

        <div className="space-y-3 ml-10">
          <div>
            <Input
              type="text"
              label={t(`${translationPrefix}.useCaseTitle`)}
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              placeholder={t(`${translationPrefix}.useCaseTitlePlaceholder`)}
            />
          </div>

          <div>
            <label className="text-xs font-medium text-500 uppercase tracking-wide mb-1 block">
              {t(`${translationPrefix}.useCaseDescription`)}
            </label>
            <VariableEditor
              value={editedDescription}
              setValue={setEditedDescription}
              variables={variables}
              placeholder={t(`${translationPrefix}.useCaseDescriptionPlaceholder`)}
            />
          </div>

          <div>
            <label className="text-xs font-medium text-500 uppercase tracking-wide mb-2 block">
              {t(`${translationPrefix}.whatGailDoes`)}
            </label>
            <div className="space-y-2">
              {editedActions.map((action, actionIndex) => (
                <div key={action._key} className="flex items-start gap-2">
                  <Icon icon="arrow-right" className="w-3 h-3 text-primary flex-shrink-0 mt-3" />
                  <div className="flex-1">
                    <VariableEditor
                      value={action.description}
                      setValue={(value) => handleActionChange(actionIndex, value)}
                      variables={variables}
                      placeholder={t(`${translationPrefix}.actionPlaceholder`)}
                      compact
                    />
                  </div>
                  <Button
                    type="none"
                    onClick={() => handleRemoveAction(actionIndex)}
                    className="p-1 text-muted-foreground hover:text-destructive transition-colors mt-2"
                  >
                    <Icon icon="xmark" className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="none"
                onClick={handleAddAction}
                className={clsx(
                  "flex items-center gap-2 text-sm text-primary",
                  "hover:text-primary/80 transition-colors"
                )}
              >
                <Icon icon="plus" className="w-3 h-3" />
                {t(`${translationPrefix}.addAction`)}
              </Button>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-200">
          <Button type="tertiary" onClick={handleCancel} disabled={isSaving}>
            {t(`${translationPrefix}.cancel`)}
          </Button>
          <Button type="primary" onClick={handleSave} disabled={isSaving || !editedTitle.trim()}>
            {isSaving ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t(`${translationPrefix}.saving`)}
              </span>
            ) : (
              t(`${translationPrefix}.save`)
            )}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <AccordionItem
      value={useCase.id}
      className={clsx(
        "border border-200 rounded-xl overflow-hidden",
        "bg-panel hover:bg-50 dark:hover:bg-800/50 transition-colors"
      )}
    >
      <AccordionTrigger className="px-4 py-3 hover:no-underline">
        <div className="flex items-center gap-3 w-full">
          <div
            className={clsx(
              "w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0",
              "bg-primary/10 text-primary text-xs font-semibold"
            )}
          >
            {index + 1}
          </div>
          <div className="flex-1 text-left">
            <p className="font-medium text-foreground">{useCase.title}</p>
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        <div className="ml-10 space-y-3">
          <p className="text-sm text-muted-foreground">
            {renderContentWithVariables(useCase.description, ct)}
          </p>
          {actions.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-500 uppercase tracking-wide">
                {t(`${translationPrefix}.whatGailDoes`)}
              </p>
              <ul className="space-y-1.5">
                {actions.map((action) => (
                  <li key={action._key} className="flex items-start gap-2 text-sm text-700">
                    <Icon icon="arrow-right" className="w-3 h-3 text-primary mt-1 flex-shrink-0" />
                    <span>{renderContentWithVariables(action.description, ct)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Edit/Delete buttons */}
          <div className="flex items-center gap-2 pt-2 border-t border-200">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
              }}
              className={clsx(
                "flex items-center gap-1.5 px-2 py-1 rounded-full text-sm",
                "text-muted-foreground hover:text-primary hover:bg-primary/10",
                "transition-colors"
              )}
            >
              <Icon icon="pencil" className="w-3 h-3" />
              {t(`${translationPrefix}.edit`)}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(useCase.id);
              }}
              className={clsx(
                "flex items-center gap-1.5 px-2 py-1 rounded-full text-sm",
                "text-muted-foreground hover:text-destructive hover:bg-destructive/10",
                "transition-colors"
              )}
            >
              <Icon icon="trash" className="w-3 h-3" />
              {t(`${translationPrefix}.delete`)}
            </button>
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

// Add Use Case form component (used in inbound mode)
function AddUseCaseForm({
  onAdd,
  onCancel,
  isSaving,
  variables,
  translationPrefix,
}: {
  readonly onAdd: (useCase: Omit<UseCase, "id" | "status">) => Promise<void>;
  readonly onCancel: () => void;
  readonly isSaving: boolean;
  readonly variables: string[];
  readonly translationPrefix: string;
}) {
  const { t } = useTranslation("onboarding");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const actionKeyCounter = useRef(0);
  const [actions, setActions] = useState<UseCaseAction[]>([
    { description: "", _key: `action-${++actionKeyCounter.current}` },
  ]);

  const handleSubmit = async () => {
    await onAdd({
      title,
      description,
      actions: actions.filter((a) => a.description.trim()),
    });
  };

  const handleActionChange = (index: number, value: string) => {
    const newActions = [...actions];
    newActions[index] = { ...newActions[index], description: value };
    setActions(newActions);
  };

  const handleAddAction = () => {
    setActions([...actions, { description: "", _key: `action-${++actionKeyCounter.current}` }]);
  };

  const handleRemoveAction = (index: number) => {
    if (actions.length > 1) {
      setActions(actions.filter((_, i) => i !== index));
    }
  };

  return (
    <div
      className={clsx(
        "border-2 border-dashed border-primary/30 rounded-xl",
        "bg-primary/5 p-4 space-y-4"
      )}
    >
      <div className="flex items-center gap-2">
        <Icon icon="plus" className="w-4 h-4 text-primary" />
        <span className="text-sm font-medium text-foreground">
          {t(`${translationPrefix}.newScenario`)}
        </span>
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-xs font-medium text-500 uppercase tracking-wide mb-1 block">
            {t(`${translationPrefix}.useCaseTitle`)}
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={clsx(
              "w-full px-3 py-2 rounded-lg",
              "bg-panel border border-200",
              "text-sm text-foreground",
              "focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50"
            )}
            placeholder={t(`${translationPrefix}.useCaseTitlePlaceholder`)}
          />
        </div>

        <div>
          <label className="text-xs font-medium text-500 uppercase tracking-wide mb-1 block">
            {t(`${translationPrefix}.useCaseDescription`)}
          </label>
          <VariableEditor
            value={description}
            setValue={setDescription}
            variables={variables}
            placeholder={t(`${translationPrefix}.useCaseDescriptionPlaceholder`)}
          />
        </div>

        <div>
          <label className="text-xs font-medium text-500 uppercase tracking-wide mb-2 block">
            {t(`${translationPrefix}.whatGailDoes`)}
          </label>
          <div className="space-y-2">
            {actions.map((action, actionIndex) => (
              <div key={action._key} className="flex items-start gap-2">
                <Icon icon="arrow-right" className="w-3 h-3 text-primary flex-shrink-0 mt-3" />
                <div className="flex-1">
                  <VariableEditor
                    value={action.description}
                    setValue={(value) => handleActionChange(actionIndex, value)}
                    variables={variables}
                    placeholder={t(`${translationPrefix}.actionPlaceholder`)}
                    compact
                  />
                </div>
                {actions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveAction(actionIndex)}
                    className="p-1 text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <Icon icon="xmark" className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddAction}
              className={clsx(
                "flex items-center gap-2 text-sm text-primary",
                "hover:text-primary/80 transition-colors"
              )}
            >
              <Icon icon="plus" className="w-3 h-3" />
              {t(`${translationPrefix}.addAction`)}
            </button>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-200">
        <Button type="tertiary" onClick={onCancel} disabled={isSaving}>
          {t(`${translationPrefix}.cancel`)}
        </Button>
        <Button type="primary" onClick={handleSubmit} disabled={isSaving || !title.trim()}>
          {isSaving ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              {t(`${translationPrefix}.saving`)}
            </span>
          ) : (
            t(`${translationPrefix}.addScenario`)
          )}
        </Button>
      </div>
    </div>
  );
}

// Mode-specific configuration
const MODE_CONFIG = {
  inbound: {
    headerIcon: <Icon icon="scroll" className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />,
    greetingIcon: <Icon icon="comments" className="w-4 h-4 text-white" />,
    goodbyeIcon: <Icon icon="phone-slash" className="w-4 h-4 text-white" />,
    showVoicemail: false,
    showAddScenario: true,
  },
  outbound: {
    headerIcon: <Icon icon="scroll" className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />,
    greetingIcon: <Icon icon="message" className="w-4 h-4 text-white" />,
    goodbyeIcon: <Icon icon="phone-slash" className="w-4 h-4 text-white" />,
    showVoicemail: true,
    showAddScenario: true,
  },
} as const;

export function ScriptPreviewSection({
  script,
  useCases,
  onScriptUpdate,
  onUseCasesUpdate,
  mode = "inbound",
}: ScriptPreviewSectionProps) {
  const { t } = useTranslation("onboarding");
  const { org } = useOrg();
  const { setAlert } = useAlert();
  const [expandedUseCases, setExpandedUseCases] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [variables, setVariables] = useState<string[]>([]);

  const translationPrefix = getTranslationPrefix(mode);
  const config = MODE_CONFIG[mode];
  const scriptApiVersion = scriptApiVersionFromFeatures(org?.features);

  // Fetch available variables
  useEffect(() => {
    const fetchVariables = async () => {
      try {
        const response = await apiLive<VariablesResponse>(
          `${GAIL_API}/v1/contacts/variable_fields`
        );
        if (response) {
          setVariables(response);
        }
      } catch (error) {
        console.error("Failed to fetch variables:", error);
      }
    };

    fetchVariables();
  }, []);

  const handleSaveGreeting = useCallback(
    async (newGreeting: string) => {
      if (!script) return;

      setIsSaving(true);
      try {
        const response = await putOnboardingPhoneScript(
          { ...withPreviewUseCases(script, useCases), greeting: newGreeting },
          scriptApiVersion
        );

        if (response) {
          onScriptUpdate?.(response);
          setAlert({
            status: "success",
            message: t(`${translationPrefix}.greetingSaved`),
          });
        }
      } catch (error) {
        console.error("Failed to save greeting:", error);
        setAlert({
          status: "error",
          message: t(`${translationPrefix}.saveFailed`),
        });
      } finally {
        setIsSaving(false);
      }
    },
    [script, useCases, scriptApiVersion, onScriptUpdate, setAlert, t, translationPrefix]
  );

  const handleSaveVoicemail = useCallback(
    async (newVoicemail: string) => {
      if (!script) return;

      setIsSaving(true);
      try {
        const response = await putOnboardingPhoneScript(
          { ...withPreviewUseCases(script, useCases), voicemail: newVoicemail },
          scriptApiVersion
        );

        if (response) {
          onScriptUpdate?.(response);
          setAlert({
            status: "success",
            message: t(`${translationPrefix}.voicemailSaved`),
          });
        }
      } catch (error) {
        console.error("Failed to save voicemail:", error);
        setAlert({
          status: "error",
          message: t(`${translationPrefix}.saveFailed`),
        });
      } finally {
        setIsSaving(false);
      }
    },
    [script, useCases, scriptApiVersion, onScriptUpdate, setAlert, t, translationPrefix]
  );

  const handleSaveGoodbye = useCallback(
    async (newGoodbye: string) => {
      if (!script) return;

      setIsSaving(true);
      try {
        const response = await putOnboardingPhoneScript(
          { ...withPreviewUseCases(script, useCases), goodbye: newGoodbye },
          scriptApiVersion
        );

        if (response) {
          onScriptUpdate?.(response);
          setAlert({
            status: "success",
            message: t(`${translationPrefix}.goodbyeSaved`),
          });
        }
      } catch (error) {
        console.error("Failed to save goodbye:", error);
        setAlert({
          status: "error",
          message: t(`${translationPrefix}.saveFailed`),
        });
      } finally {
        setIsSaving(false);
      }
    },
    [script, useCases, scriptApiVersion, onScriptUpdate, setAlert, t, translationPrefix]
  );

  const handleSaveUseCase = useCallback(
    async (updatedUseCase: UseCase) => {
      setIsSaving(true);
      try {
        if (scriptApiVersion === 2) {
          if (!script) return;
          const updatedUseCases = useCases.map((useCase) =>
            useCase.id === updatedUseCase.id ? updatedUseCase : useCase
          );
          const updatedScript = {
            ...withPreviewUseCases(script, updatedUseCases),
            useCases: updatedUseCases,
            useCaseAssociations: associationsFromEmbeddedUseCases(updatedUseCases),
          };
          const response = await putOnboardingPhoneScript(updatedScript, 2);
          if (response) {
            onScriptUpdate?.(response);
            onUseCasesUpdate?.(updatedUseCases);
            setAlert({
              status: "success",
              message: t(`${translationPrefix}.useCaseSaved`),
            });
          }
          return;
        }

        const updatePayload = {
          title: updatedUseCase.title,
          description: updatedUseCase.description,
          actions: updatedUseCase.actions,
        };
        const response = await apiFetch<UseCase>(
          `${GAIL_API}/v1/use_cases/${updatedUseCase.id}`,
          "PUT",
          {
          body: updatePayload,
        },
        );

        if (response) {
          const updatedUseCases = useCases.map((uc) => (uc.id === response.id ? response : uc));
          onUseCasesUpdate?.(updatedUseCases);
          setAlert({
            status: "success",
            message: t(`${translationPrefix}.useCaseSaved`),
          });
        }
      } catch (error) {
        console.error("Failed to save use case:", error);
        setAlert({
          status: "error",
          message: t(`${translationPrefix}.saveFailed`),
        });
      } finally {
        setIsSaving(false);
      }
    },
    [
      script,
      useCases,
      scriptApiVersion,
      onScriptUpdate,
      onUseCasesUpdate,
      setAlert,
      t,
      translationPrefix,
    ]
  );

  const handleDeleteUseCase = useCallback(
    async (useCaseId: string) => {
      if (!script) return;

      setIsSaving(true);
      try {
        const updatedUseCases = useCases.filter((useCase) => useCase.id !== useCaseId);
        const nextScript =
          scriptApiVersion === 2
            ? {
                ...withPreviewUseCases(script, updatedUseCases),
                useCases: updatedUseCases,
                useCaseAssociations: associationsFromEmbeddedUseCases(updatedUseCases),
              }
            : {
                ...script,
                useCaseAssociations:
                  script.useCaseAssociations?.filter((association) => association.id !== useCaseId) ??
                  [],
              };

        const response = await putOnboardingPhoneScript(nextScript, scriptApiVersion);

        if (response) {
          onScriptUpdate?.(response);
          onUseCasesUpdate?.(updatedUseCases);
          setAlert({
            status: "success",
            message: t(`${translationPrefix}.useCaseDeleted`),
          });
        }
      } catch (error) {
        console.error("Failed to delete use case:", error);
        setAlert({
          status: "error",
          message: t(`${translationPrefix}.deleteFailed`),
        });
      } finally {
        setIsSaving(false);
      }
    },
    [
      script,
      useCases,
      scriptApiVersion,
      onScriptUpdate,
      onUseCasesUpdate,
      setAlert,
      t,
      translationPrefix,
    ]
  );

  const handleAddUseCase = useCallback(
    async (newUseCase: Omit<UseCase, "id" | "status">) => {
      if (!script) return;

      setIsSaving(true);
      try {
        if (scriptApiVersion === 2) {
          const createdUseCase: UseCase = {
            ...newUseCase,
            id: uuidv7(),
            status: "active",
          };
          const updatedUseCases = [...useCases, createdUseCase];
          const updatedScript = {
            ...withPreviewUseCases(script, updatedUseCases),
            useCases: updatedUseCases,
            useCaseAssociations: associationsFromEmbeddedUseCases(updatedUseCases),
          };
          const scriptResponse = await putOnboardingPhoneScript(updatedScript, 2);
          if (scriptResponse) {
            onScriptUpdate?.(scriptResponse);
            onUseCasesUpdate?.(updatedUseCases);
            setAlert({
              status: "success",
              message: t(`${translationPrefix}.scenarioAdded`),
            });
            setShowAddForm(false);
          }
          return;
        }

        const useCaseResponse = await apiFetch<UseCase>(
          `${process.env.NEXT_PUBLIC_GAIL_API}/v1/use_cases`,
          "POST",
          {
          body: newUseCase,
        },
        );

        if (useCaseResponse) {
          const newAssociation = {
            id: useCaseResponse.id,
            order: (script.useCaseAssociations?.length ?? 0) + 1,
            status: "active" as const,
          };

          const updatedAssociations = [...(script.useCaseAssociations ?? []), newAssociation];

          const scriptResponse = await putOnboardingPhoneScript(
            { ...script, useCaseAssociations: updatedAssociations },
            1
          );

          if (scriptResponse) {
            onScriptUpdate?.(scriptResponse);
            onUseCasesUpdate?.([...useCases, useCaseResponse]);
            setAlert({
              status: "success",
              message: t(`${translationPrefix}.scenarioAdded`),
            });
            setShowAddForm(false);
          }
        }
      } catch (error) {
        console.error("Failed to add use case:", error);
        setAlert({
          status: "error",
          message: t(`${translationPrefix}.saveFailed`),
        });
      } finally {
        setIsSaving(false);
      }
    },
    [
      script,
      useCases,
      scriptApiVersion,
      onScriptUpdate,
      onUseCasesUpdate,
      setAlert,
      t,
      translationPrefix,
    ]
  );

  if (!script) {
    return (
      <div
        className={clsx(
          "rounded-2xl border border-amber-500/50 bg-amber-500/5 p-6",
          "text-center space-y-2"
        )}
      >
        <Icon icon="circle-info" className="w-6 h-6 text-amber-500 mx-auto" />
        <p className="text-sm text-muted-foreground">{t(`${translationPrefix}.noScript`)}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-center gap-3">
        <div
          className={clsx(
            "w-10 h-10 rounded-xl flex items-center justify-center",
            "bg-emerald-100 dark:bg-emerald-900/30"
          )}
        >
          {config.headerIcon}
        </div>
        <div>
          <h3 className="font-semibold text-foreground">{t(`${translationPrefix}.title`)}</h3>
          <p className="text-sm text-muted-foreground">{t(`${translationPrefix}.subtitle`)}</p>
        </div>
      </div>

      {/* Script Content */}
      <div className={clsx("rounded-2xl border border-200 bg-panel p-6 space-y-6")}>
        {/* Greeting */}
        <EditableSpeechBubble
          content={script.greeting}
          label={t(`${translationPrefix}.greeting`)}
          icon={config.greetingIcon}
          colorClass="bg-emerald-500"
          onSave={handleSaveGreeting}
          isSaving={isSaving}
          variables={variables}
          translationPrefix={translationPrefix}
        />

        {/* Use Cases */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon icon="list-check" className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-700">
                {t(`${translationPrefix}.useCasesLabel`, {
                  count: useCases.length,
                })}
              </span>
            </div>
          </div>

          {useCases.length > 0 && (
            <Accordion
              type="multiple"
              value={expandedUseCases}
              onValueChange={setExpandedUseCases}
              className="space-y-2"
            >
              {useCases.map((useCase, index) => (
                <EditableUseCaseCard
                  key={useCase.id}
                  useCase={useCase}
                  index={index}
                  onSave={handleSaveUseCase}
                  onDelete={handleDeleteUseCase}
                  isSaving={isSaving}
                  variables={variables}
                  translationPrefix={translationPrefix}
                />
              ))}
            </Accordion>
          )}

          {/* Add Use Case (inbound mode) */}
          {config.showAddScenario && (
            <>
              {showAddForm ? (
                <AddUseCaseForm
                  onAdd={handleAddUseCase}
                  onCancel={() => setShowAddForm(false)}
                  isSaving={isSaving}
                  variables={variables}
                  translationPrefix={translationPrefix}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setShowAddForm(true)}
                  className={clsx(
                    "w-full flex items-center justify-center gap-2 p-3 rounded-xl",
                    "border-2 border-dashed border-300 dark:border-600",
                    "text-muted-foreground hover:text-foreground",
                    "hover:border-primary/50 hover:bg-primary/5",
                    "transition-all duration-200"
                  )}
                >
                  <Icon icon="plus" className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {t(`${translationPrefix}.addScenario`)}
                  </span>
                </button>
              )}
            </>
          )}
        </div>

        {/* Goodbye */}
        <EditableSpeechBubble
          content={script.goodbye}
          label={t(`${translationPrefix}.goodbye`)}
          icon={config.goodbyeIcon}
          colorClass="bg-blue-500"
          onSave={handleSaveGoodbye}
          isSaving={isSaving}
          variables={variables}
          translationPrefix={translationPrefix}
        />

        {/* Voicemail (outbound mode) */}
        {config.showVoicemail && script.voicemail && (
          <EditableSpeechBubble
            content={script.voicemail}
            label={t(`${translationPrefix}.voicemail`)}
            icon={<Icon icon="voicemail" className="w-4 h-4 text-white" />}
            colorClass="bg-amber-500"
            onSave={handleSaveVoicemail}
            isSaving={isSaving}
            variables={variables}
            translationPrefix={translationPrefix}
          />
        )}
      </div>
    </div>
  );
}
